// /q/:token: the QuoteLink, the customer's page. It carries the selling dealer's
// name, color and mark, with Phoneware only as a small Powered by mark at the
// foot. The customer reads the quote, types a name and email, draws a signature
// and accepts; Lens records the audit trail and stamps it on the signed PDF.
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { formatCents, formatTerm, type Billing, type PublicQuote, type PublicQuoteResponse, type QuoteType } from "@phoneware/lens-domain";
import { ApiError, type LensApi } from "../api.js";
import { Icon } from "../icons.js";
import { BILLING, errorText, firstName, isEmail, stamp } from "../format.js";
import { DealerMark, Wordmark, useTitle } from "../ui.js";
import { Appearance } from "../appearance.js";

const TYPE_WORDS: Record<QuoteType, string> = { new: "New service", change: "Change to your service", renewal: "Renewal" };

export interface PadHandle {
  png(): string;
}

/** The drawn signature. Pointer events, so a finger, a stylus and a mouse all sign. */
const SignaturePad = forwardRef<PadHandle, { onInk: (inked: boolean) => void }>(function SignaturePad({ onInk }, ref) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const inked = useRef(false);
  const width = useRef(0);

  useImperativeHandle(ref, () => ({ png: () => canvas.current?.toDataURL("image/png") ?? "" }), []);

  function clear(): void {
    const cv = canvas.current;
    const ctx = cv?.getContext("2d");
    if (cv && ctx) ctx.clearRect(0, 0, cv.width, cv.height);
    inked.current = false;
    onInk(false);
  }

  useEffect(() => {
    const cv = canvas.current;
    if (!cv) return;
    // Size the drawing surface to the box and the screen's pixel density. Only a real width change
    // resets it: a phone keyboard opening changes the window height, which must not wipe a signature.
    const fit = () => {
      const box = cv.getBoundingClientRect();
      if (width.current !== 0 && Math.round(box.width) === width.current) return;
      width.current = Math.round(box.width);
      const dpr = window.devicePixelRatio || 1;
      cv.width = Math.max(1, Math.round(box.width * dpr));
      cv.height = Math.max(1, Math.round(box.height * dpr));
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0A1620";
      inked.current = false;
      onInk(false);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [onInk]);

  const at = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const box = e.currentTarget.getBoundingClientRect();
    return [e.clientX - box.left, e.clientY - box.top];
  };
  const stop = () => {
    drawing.current = false;
  };

  return (
    <div className="ql-pad">
      <canvas
        ref={canvas}
        role="img"
        aria-labelledby="ql-sign-label"
        data-testid="ql-signature"
        onPointerDown={(e) => {
          const ctx = e.currentTarget.getContext("2d");
          if (!ctx) return;
          e.preventDefault();
          drawing.current = true;
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            // A pointer the browser no longer tracks; drawing still follows moves over the pad.
          }
          const [x, y] = at(e);
          ctx.beginPath();
          ctx.moveTo(x, y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = e.currentTarget.getContext("2d");
          if (!ctx) return;
          const [x, y] = at(e);
          ctx.lineTo(x, y);
          ctx.stroke();
          if (!inked.current) {
            inked.current = true;
            onInk(true);
          }
        }}
        onPointerUp={stop}
        onPointerCancel={stop}
        onPointerLeave={stop}
      />
      <button type="button" className="ql-clear" onClick={clear}>
        Clear
      </button>
      <i className="ql-baseline" aria-hidden="true" />
    </div>
  );
});

function LineList({ quote, billing }: { quote: PublicQuote; billing: Billing }): React.ReactElement | null {
  const lines = quote.lines.filter((l) => l.billing === billing);
  if (!lines.length) return null;
  return (
    <section className="ql-sec">
      <h2>{billing === "monthly" ? "Every month" : BILLING[billing]}</h2>
      <ul className="ql-lines">
        {lines.map((l, i) => (
          <li key={`${l.name}-${i}`}>
            <span>
              <b>{l.name}</b>
              <span className="ql-q">
                {l.qty} × {formatCents(l.unitPrice)} {l.unit}
              </span>
            </span>
            <span className="num">{formatCents(l.qty * l.unitPrice)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface Signed {
  email: string;
  drawnPng: string;
}

function Confirmation({ res, signed, pdfUrl }: { res: PublicQuoteResponse; signed: Signed | null; pdfUrl: string }): React.ReactElement {
  const q = res.quote;
  const name = q.signedBy ?? "";
  return (
    <section className="ql-done" data-testid="ql-signed">
      <div className="ql-check">
        <Icon name="check" size={28} />
      </div>
      <h1>Signed. Thank you{name ? `, ${firstName(name)}` : ""}.</h1>
      <p>{q.dealer.name} has your signed proposal. Keep the signed PDF for your records.</p>
      <dl className="ql-audit">
        <div>
          <dt>Signed by</dt>
          <dd>{name}</dd>
        </div>
        {signed ? (
          <div>
            <dt>Email</dt>
            <dd>{signed.email}</dd>
          </div>
        ) : null}
        {q.signedAt ? (
          <div>
            <dt>When</dt>
            <dd>{stamp(q.signedAt)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Document fingerprint</dt>
          <dd className="mono">SHA-256 {res.documentSha256}</dd>
        </div>
      </dl>
      {signed?.drawnPng ? <img className="ql-sigimg" alt="Your signature" src={signed.drawnPng} /> : null}
      <a className="btn ql-btn" href={pdfUrl} target="_blank" rel="noreferrer">
        <Icon name="file-down" size={16} />
        Download the signed PDF
      </a>
    </section>
  );
}

type Load = { kind: "loading" } | { kind: "missing" } | { kind: "failed"; message: string } | { kind: "ready"; res: PublicQuoteResponse };

export function QuoteLinkPage({ api, token }: { api: LensApi; token: string }): React.ReactElement {
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [signed, setSigned] = useState<Signed | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [inked, setInked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pad = useRef<PadHandle>(null);

  useTitle(load.kind === "ready" ? `Proposal ${load.res.quote.number}` : "Proposal");

  useEffect(() => {
    let live = true;
    api.publicQuote(token).then(
      (res) => {
        if (live) setLoad({ kind: "ready", res });
      },
      (err: unknown) => {
        if (!live) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 410)) setLoad({ kind: "missing" });
        else setLoad({ kind: "failed", message: errorText(err) });
      },
    );
    return () => {
      live = false;
    };
  }, [api, token]);

  const pdfUrl = api.publicPdfUrl(token);
  const foot = (
    <footer className="ql-foot">
      <span>Powered by</span>
      <Wordmark className="wm-xs" />
    </footer>
  );
  const appearance = <div className="ql-appearance"><Appearance /></div>;

  if (load.kind !== "ready") {
    return (
      <main className="ql">
        {appearance}
        <section className="ql-doc">
          <section className="ql-done">
            {load.kind === "loading" ? (
              <p>Opening the proposal.</p>
            ) : load.kind === "missing" ? (
              <>
                <h1>This link does not work</h1>
                <p>Ask whoever sent it for a fresh one.</p>
              </>
            ) : (
              <>
                <h1>The proposal did not open</h1>
                <p>{load.message}</p>
              </>
            )}
          </section>
        </section>
        {foot}
        </main>
    );
  }

  const { res } = load;
  const q = res.quote;
  const brandStyle = { "--brand": q.dealer.brand.color } as React.CSSProperties;
  const expired = !q.signed && new Date(q.expiresAt).getTime() < Date.now();
  const ready = name.trim().length > 1 && isEmail(email) && inked && accepted;

  async function sign(e: React.FormEvent) {
    e.preventDefault();
    if (!ready || busy || !pad.current) return;
    const drawnPng = pad.current.png();
    setBusy(true);
    setError(null);
    try {
      const next = await api.signQuote(token, { name: name.trim(), email: email.trim(), drawnPng, accepted: true, documentSha256: res.documentSha256 });
      setSigned({ email: email.trim(), drawnPng });
      setLoad({ kind: "ready", res: next });
      window.scrollTo(0, 0);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="ql" style={brandStyle}>
      <header className="ql-top">
        <div className="ql-brand">
          <DealerMark name={q.dealer.name} brand={q.dealer.brand} size={32} onColor />
          <b>{q.dealer.name}</b>
        </div>
        {q.dealer.phone ? (
          <a className="ql-call" href={`tel:${q.dealer.phone.replace(/[^\d+]/g, "")}`} aria-label={`Call ${q.dealer.name}`}>
            <Icon name="phone" size={15} />
            <span>{q.dealer.phone}</span>
          </a>
        ) : null}
      </header>
      {appearance}
      <section className="ql-doc">
        {q.signed ? (
          <Confirmation res={res} signed={signed} pdfUrl={pdfUrl} />
        ) : (
          <>
            <p className="ql-id mono">Proposal {q.number}</p>
            <h1>Proposal for {q.customerName}</h1>
            <p className="ql-meta">
              Prepared by {q.repName} · {TYPE_WORDS[q.type]} · {formatTerm(q.term)}
            </p>
            {q.pdfNotes ? <p className="ql-note">{q.pdfNotes}</p> : null}
            <LineList quote={q} billing="monthly" />
            <LineList quote={q} billing="one_time" />
            <section className="ql-sec ql-totals">
              <dl>
                <div className="big">
                  <dt>Each month</dt>
                  <dd>{formatCents(q.totals.monthly)}</dd>
                </div>
                <div className="big">
                  <dt>Due at signing</dt>
                  <dd>{formatCents(q.totals.dueAtSigning)}</dd>
                </div>
              </dl>
              <p className="ql-small">{q.taxNote}</p>
            </section>
            <section className="ql-sec">
              <h2>Terms</h2>
              <p className="ql-small">
                Contract term: {formatTerm(q.term)}. {expired ? "This proposal expired on" : "You can sign this proposal until"}{" "}
                {new Date(q.expiresAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.
              </p>
            </section>
            <a className="ql-pdf" href={pdfUrl} target="_blank" rel="noreferrer">
              <Icon name="file-text" size={15} />
              See it as a PDF
            </a>
            {expired ? (
              <section className="ql-sec">
                <p className="ql-small">This proposal is no longer open for signing. Ask {q.repName} for a fresh link.</p>
              </section>
            ) : (
              <form className="ql-sec ql-sign" noValidate onSubmit={(e) => void sign(e)}>
                <h2>Accept and sign</h2>
                <label className="ql-field">
                  <span>Your full name</span>
                  <input className="input" autoComplete="name" placeholder="Type your name" data-testid="ql-name" value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="ql-field">
                  <span>Your email</span>
                  <input
                    className="input"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    data-testid="ql-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <div className="ql-field">
                  <span id="ql-sign-label">Sign here</span>
                  <SignaturePad ref={pad} onInk={setInked} />
                </div>
                <label className="ql-agree">
                  <input type="checkbox" data-testid="ql-accept" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                  <span>
                    I accept this proposal for {q.customerName}, {q.term === "month_to_month" ? "month to month" : `on a ${q.term} month term`}.
                  </span>
                </label>
                {error ? (
                  <p className="ql-error" role="alert">
                    {error}
                  </p>
                ) : null}
                <button className="btn ql-btn" type="submit" disabled={!ready || busy} data-testid="ql-submit">
                  <Icon name="pen-line" size={16} />
                  {busy ? "Signing" : "Accept and sign"}
                </button>
                <p className="ql-small">
                  When you sign, we record your name, your email, the time, your IP address, your browser and a fingerprint of this document, and print
                  them on the signed PDF.
                </p>
              </form>
            )}
          </>
        )}
      </section>
      {q.signed ? null : (
        <p className="ql-ask">
          Questions? {q.dealer.phone ? `Call ${q.dealer.name} at ${q.dealer.phone}` : `Ask ${q.repName}`} or reply to the email this came in.
        </p>
      )}
      {foot}
    </main>
  );
}
